// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface StepConfig {
  id: string;
  label: string;
  /** True when the step cannot be acted on yet — it is still reachable as a read-only preview. */
  blocked?: boolean;
  /** True when the step has been completed. */
  complete?: boolean;
}

interface StepperProps {
  steps: StepConfig[];
  currentStepId: string;
  onNavigate: (id: string) => void;
  children: React.ReactNode;
  /** Accessible name for the step list. */
  label?: string;
  testId?: string;
  /** Steps after this one render disabled — they open only once reached in order. */
  lastReachableStepId?: string;
  /** Tighter chips, for a stepper inside a dialog. */
  compact?: boolean;
}

export function Stepper({
  steps,
  currentStepId,
  onNavigate,
  children,
  label = 'Install steps',
  testId = 'install-stepper',
  lastReachableStepId,
  compact = false,
}: StepperProps) {
  const lastReachableIdx = lastReachableStepId
    ? steps.findIndex((s) => s.id === lastReachableStepId)
    : steps.length - 1;
  const currentStepRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    currentStepRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [currentStepId]);

  return (
    <div className={compact ? 'space-y-5' : 'space-y-6'} data-testid={testId}>
      {/* Chips wrap onto more rows rather than scrolling; the overflow only
          engages if a single chip is wider than the viewport. */}
      <nav aria-label={label} className="overflow-x-auto">
        <ol
          className={cn('flex flex-wrap items-center gap-y-1', compact ? 'gap-x-0.5' : 'gap-x-1')}
        >
          {steps.map((step, idx) => {
            const isCurrent = step.id === currentStepId;
            const unreachable = idx > lastReachableIdx;

            return (
              <li key={step.id}>
                <button
                  ref={isCurrent ? currentStepRef : undefined}
                  type="button"
                  aria-current={isCurrent ? 'step' : undefined}
                  // Every step up to lastReachableStepId is reachable (all of them
                  // when it is unset); the step on screen has nothing to navigate to.
                  disabled={isCurrent || unreachable}
                  onClick={() => !isCurrent && onNavigate(step.id)}
                  data-testid={`step-nav-${step.id}`}
                  className={cn(
                    // Touch target: h-11 on mobile (≥44px), h-9 on md+
                    'flex items-center gap-1.5 rounded-md px-2 h-11 md:h-9 text-xs transition-colors',
                    compact && 'gap-1 px-1.5 md:h-8',
                    isCurrent && 'bg-muted font-semibold text-foreground',
                    !isCurrent && 'font-medium',
                    !isCurrent && !unreachable && 'hover:bg-muted',
                    !isCurrent && step.complete && 'text-primary',
                    !isCurrent && !step.complete && 'text-muted-foreground',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                      isCurrent && 'bg-primary text-primary-foreground',
                      !isCurrent && step.complete && 'bg-primary/20 text-primary',
                      !isCurrent && !step.complete && 'bg-muted text-muted-foreground',
                      // Shape, not color, separates a blocked upcoming step from
                      // one that is merely later in the flow.
                      !isCurrent && !step.complete && step.blocked && 'border border-dashed',
                    )}
                  >
                    {step.complete && !isCurrent ? '✓' : idx + 1}
                  </span>
                  <span>{step.label}</span>
                  {step.blocked && !step.complete && (
                    <span className="sr-only">(preview only — blocked)</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="space-y-4" data-testid={`step-content-${currentStepId}`}>
        {children}
      </div>
    </div>
  );
}
